/* ============================================================================
   mud-pagination — molecule
   ----------------------------------------------------------------------------
   Renders a navigation landmark with prev / next buttons surrounding a list
   of numbered page buttons. Sizes (sm / md) follow the Figma breakpoint axis.
   ============================================================================ */

:host {
  --_gap: var(--pagination-container-gap-md);
  --_inner-gap: var(--pagination-container-inner-gap-md);
  --_item-size: var(--pagination-item-size-md);
  --_ellipsis-size: var(--pagination-ellipsis-size-md);
  --_nav-height: var(--pagination-nav-height-md);
  --_nav-min-width: var(--pagination-nav-min-width-md);
  --_nav-pad-start: var(--pagination-nav-padding-inline-start-md);
  --_nav-pad-end: var(--pagination-nav-padding-inline-end-md);
  --_nav-icon-size: var(--pagination-nav-icon-size-md);
  --_show-nav-label: inline-flex;

  display: inline-flex;
  vertical-align: middle;
  font-family: var(--pagination-item-font-family);
  font-size: var(--pagination-item-font-size);
  line-height: var(--pagination-item-line-height);
  font-weight: var(--pagination-item-font-weight);
}

:host([size='sm']) {
  --_gap: var(--pagination-container-gap-sm);
  --_inner-gap: var(--pagination-container-inner-gap-sm);
  --_item-size: var(--pagination-item-size-sm);
  --_ellipsis-size: var(--pagination-ellipsis-size-sm);
  --_nav-height: var(--pagination-nav-height-sm);
  --_nav-min-width: var(--pagination-nav-min-width-sm);
  --_nav-pad-start: var(--pagination-nav-padding-inline-start-sm);
  --_nav-pad-end: var(--pagination-nav-padding-inline-end-sm);
  --_nav-icon-size: var(--pagination-nav-icon-size-sm);
  --_show-nav-label: none;
}

/* ---------- Layout ---------- */

.root {
  display: inline-flex;
  align-items: center;
  gap: var(--_gap);
  padding: 0;
}

.pages {
  list-style: none;
  margin: 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: var(--_inner-gap);
}

.item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* ---------- Page buttons ---------- */

.page-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--_item-size);
  block-size: var(--_item-size);
  padding: 0;
  margin: 0;

  border: var(--pagination-item-border-width-default) solid var(--pagination-item-unselected-border-default);
  border-radius: var(--pagination-item-border-radius);
  background-color: var(--pagination-item-unselected-background-default);
  color: var(--pagination-item-unselected-label-default);

  font: inherit;
  text-align: center;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  transition:
    background-color var(--pagination-container-transition-duration)
      var(--pagination-container-transition-timing-function),
    color var(--pagination-container-transition-duration) var(--pagination-container-transition-timing-function),
    box-shadow var(--pagination-container-transition-duration) var(--pagination-container-transition-timing-function),
    border-color var(--pagination-container-transition-duration) var(--pagination-container-transition-timing-function);
}

.page-button:hover {
  background-color: var(--pagination-item-unselected-background-hover);
  color: var(--pagination-item-unselected-label-hover);
}

.page-button:active {
  background-color: var(--pagination-item-unselected-background-active);
  color: var(--pagination-item-unselected-label-active);
}

.page-button:focus-visible {
  outline: none;
  border-width: var(--pagination-item-border-width-focus);
  border-color: var(--pagination-item-unselected-border-focus);
  background-color: var(--pagination-item-unselected-background-default);
}

.page-button.is-selected {
  background-color: var(--pagination-item-selected-background-default);
  color: var(--pagination-item-selected-label-default);
}

.page-button.is-selected:hover {
  background-color: var(--pagination-item-selected-background-hover);
  color: var(--pagination-item-selected-label-hover);
}

.page-button.is-selected:active {
  background-color: var(--pagination-item-selected-background-active);
  color: var(--pagination-item-selected-label-active);
}

.page-button.is-selected:focus-visible {
  background-color: var(--pagination-item-selected-background-default);
  color: var(--pagination-item-selected-label-default);
}

/* ---------- Ellipsis / overflow trigger ---------- */

.overflow-item {
  position: relative;
}

.overflow-trigger {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--_ellipsis-size);
  block-size: var(--_ellipsis-size);
  padding: 0;
  margin: 0;

  border: var(--pagination-item-border-width-default) solid var(--pagination-overflow-trigger-border-default);
  border-radius: var(--pagination-item-border-radius);
  background-color: var(--pagination-overflow-trigger-background-default);
  color: var(--pagination-overflow-trigger-label);

  font: inherit;
  text-align: center;
  cursor: pointer;
  user-select: none;
  appearance: none;
  -webkit-appearance: none;

  transition:
    background-color var(--pagination-container-transition-duration)
      var(--pagination-container-transition-timing-function),
    color var(--pagination-container-transition-duration) var(--pagination-container-transition-timing-function),
    border-color var(--pagination-container-transition-duration) var(--pagination-container-transition-timing-function);
}

.overflow-trigger:hover {
  background-color: var(--pagination-overflow-trigger-background-hover);
}

.overflow-trigger:active {
  background-color: var(--pagination-overflow-trigger-background-active);
}

.overflow-trigger:focus-visible {
  outline: none;
  border-width: var(--pagination-item-border-width-focus);
  border-color: var(--pagination-overflow-trigger-border-focus);
}

.overflow-item.is-open .overflow-trigger {
  background-color: var(--pagination-overflow-trigger-background-open);
}

/* ---------- Overflow dropdown menu ---------- */

.overflow-menu {
  position: absolute;
  top: calc(100% + var(--pagination-overflow-menu-offset));
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;

  list-style: none;
  margin: 0;
  padding-block: var(--pagination-overflow-menu-padding-block);
  padding-inline: var(--pagination-overflow-menu-padding-inline);

  display: flex;
  flex-direction: column;
  gap: var(--pagination-overflow-menu-gap);

  min-inline-size: var(--pagination-overflow-menu-min-width);
  /* Cap a large collapsed range (e.g. page 1 of 40 collapses ~34 pages) to a
     fixed height and scroll the surplus inside, instead of letting the menu run
     off the viewport. ~7.5 rows stay visible so the cut-off row hints at more. */
  max-block-size: var(--pagination-overflow-menu-max-height);
  overflow-y: auto;
  overscroll-behavior: contain;
  background-color: var(--pagination-overflow-menu-background);
  border: var(--pagination-overflow-menu-border-width) solid var(--pagination-overflow-menu-border-color);
  border-radius: var(--pagination-overflow-menu-border-radius);
  box-shadow: var(--pagination-overflow-menu-shadow);
}

.overflow-menu li {
  display: block;
}

.overflow-menu-item {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: 100%;
  block-size: var(--pagination-overflow-menu-item-size-md);
  padding-inline: var(--pagination-overflow-menu-item-padding-inline);
  margin: 0;

  border: var(--pagination-item-border-width-default) solid transparent;
  border-radius: var(--pagination-overflow-menu-item-border-radius);
  background-color: var(--pagination-overflow-menu-item-background-default);
  color: var(--pagination-overflow-menu-item-label);

  font: inherit;
  text-align: center;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  transition: background-color var(--pagination-container-transition-duration)
    var(--pagination-container-transition-timing-function);
}

:host([size='sm']) .overflow-menu-item {
  block-size: var(--pagination-overflow-menu-item-size-sm);
}

.overflow-menu-item:hover {
  background-color: var(--pagination-overflow-menu-item-background-hover);
}

.overflow-menu-item:active {
  background-color: var(--pagination-overflow-menu-item-background-active);
}

.overflow-menu-item:focus-visible {
  outline: none;
  border-width: var(--pagination-item-border-width-focus);
  border-color: var(--pagination-overflow-trigger-border-focus);
}

/* ---------- Prev / Next buttons ---------- */

.nav-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--pagination-nav-gap);

  block-size: var(--_nav-height);
  min-inline-size: var(--_nav-min-width);
  padding-block: 0;
  padding-inline-start: var(--_nav-pad-start);
  padding-inline-end: var(--_nav-pad-end);

  margin: 0;
  border: var(--pagination-item-border-width-default) solid var(--pagination-nav-border-default);
  border-radius: var(--pagination-nav-border-radius);
  background-color: var(--pagination-nav-background-default);
  color: var(--pagination-nav-label-default);

  font: inherit;
  text-align: center;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  overflow: clip;

  transition:
    background-color var(--pagination-container-transition-duration)
      var(--pagination-container-transition-timing-function),
    color var(--pagination-container-transition-duration) var(--pagination-container-transition-timing-function),
    box-shadow var(--pagination-container-transition-duration) var(--pagination-container-transition-timing-function),
    border-color var(--pagination-container-transition-duration) var(--pagination-container-transition-timing-function);
}

.nav-button:hover {
  background-color: var(--pagination-nav-background-hover);
}

.nav-button:active {
  background-color: var(--pagination-nav-background-active);
}

.nav-button:focus-visible {
  outline: none;
  border-width: var(--pagination-item-border-width-focus);
  border-color: var(--pagination-nav-border-focus);
}

.nav-button[disabled],
.nav-button.is-disabled {
  cursor: not-allowed;
  color: var(--pagination-nav-label-disabled);
  background-color: var(--pagination-nav-background-default);
}

.nav-button[disabled] .nav-icon,
.nav-button.is-disabled .nav-icon {
  color: var(--pagination-nav-icon-disabled);
}

.nav-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--_nav-icon-size);
  block-size: var(--_nav-icon-size);
  color: var(--pagination-nav-icon-default);
  flex: 0 0 auto;
}

.nav-icon ::slotted(*) {
  display: inline-flex;
  inline-size: 100%;
  block-size: 100%;
}

.nav-label {
  display: var(--_show-nav-label);
  align-items: center;
  white-space: nowrap;
  color: inherit;
}

/* Hide internal icon when consumer provided a custom one — Stencil leaves
   the slot empty until @State updates; the fallback render handles the rest. */

/* ---------- Reduced motion ---------- */

@media (prefers-reduced-motion: reduce) {
  .page-button,
  .nav-button {
    transition: none;
  }
}
